import React, { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { AlertTriangle, Check, ClipboardPaste, Trash2 } from 'lucide-react';
import DialogBase from '../../../base/DialogBase/DialogBase';

const GET_TAG_IMPORT_CONTEXT = gql`
    query GetTagImportContext {
        TagList(first: 10000) { nodes { name type } }
        ArchivedTagList(first: 10000) { nodes { name type } }
        TagTypeSettings { name }
    }
`;

const IMPORT_TAGS = gql`
    mutation ImportTags($tags: [ImportTagInput!]!) {
        ImportTags(tags: $tags)
    }
`;

export type ImportRow = {
    type: string;
    name: string;
    description: string;
};

export type ValidatedRow = ImportRow & {
    index: number;
    errors: string[];
};

const keyOf = (type: string, name: string) => `${type.trim().toLocaleLowerCase()}\u001f${name.trim().toLocaleLowerCase()}`;

export const parseTagClipboard = (text: string): ImportRow[] => text
    .replace(/\r/g, '')
    .split('\n')
    .filter(line => line.trim())
    .map(line => {
        const [type = '', name = '', ...descriptionParts] = line.split('\t');
        return {
            type: type.trim(),
            name: name.trim(),
            description: descriptionParts.join('\t').trim(),
        };
    })
    .filter((row, index) => index !== 0
        || row.type.toLocaleLowerCase() !== 'tag type'
        || row.name.toLocaleLowerCase() !== 'tag name');

export const validateTagImportRows = (
    rows: ImportRow[],
    existingTags: Array<{ type?: string | null; name?: string | null }>,
    typeNames: Array<string | null | undefined>,
): ValidatedRow[] => {
    const existingKeys = new Set<string>(existingTags.map(tag => keyOf(tag.type || '', tag.name || '')));
    const knownTypes = new Set<string>(typeNames
        .map(name => name?.trim().toLocaleLowerCase())
        .filter((name): name is string => !!name));
    const batchCounts = new Map<string, number>();
    rows.forEach(row => {
        const key = keyOf(row.type, row.name);
        batchCounts.set(key, (batchCounts.get(key) || 0) + 1);
    });

    return rows.map((row, index) => {
        const errors: string[] = [];
        if (!row.type || !row.name || !row.description) errors.push('All three fields are required');
        if (row.type && !knownTypes.has(row.type.toLocaleLowerCase())) errors.push('Unknown tag type');
        if (existingKeys.has(keyOf(row.type, row.name))) errors.push('Tag already exists');
        if ((batchCounts.get(keyOf(row.type, row.name)) || 0) > 1) errors.push('Duplicate pasted row');
        return { ...row, index, errors };
    });
};

const ImportTagsDialog = ({ onClose }: { onClose: (saved?: boolean) => void }) => {
    const [rows, setRows] = useState<ImportRow[]>([]);
    const [validationError, setValidationError] = useState('');
    const context = useQuery(GET_TAG_IMPORT_CONTEXT, { fetchPolicy: 'network-only' });
    const [importTags, importResult] = useMutation(IMPORT_TAGS);

    const validatedRows = useMemo<ValidatedRow[]>(() => {
        const existingTags = [
            ...(context.data?.TagList?.nodes || []),
            ...(context.data?.ArchivedTagList?.nodes || []),
        ].filter(Boolean);
        const typeNames = [
            ...(context.data?.TagTypeSettings || []).map((setting: any) => setting?.name),
            ...existingTags.map((tag: any) => tag?.type),
        ];
        return validateTagImportRows(rows, existingTags, typeNames);
    }, [context.data, rows]);

    const errorCount = validatedRows.filter(row => row.errors.length > 0).length;
    const paste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const parsed = parseTagClipboard(text);
            setRows(parsed);
            setValidationError(parsed.length ? '' : 'The clipboard does not contain any tag rows.');
        } catch {
            setValidationError('Could not read the clipboard. Allow clipboard access and try again.');
        }
    };

    const submit = async () => {
        if (!rows.length || errorCount > 0) return;
        setValidationError('');
        try {
            await importTags({ variables: { tags: rows } });
            onClose(true);
        } catch (error: any) {
            setValidationError(error?.message || 'Unable to import tags.');
            await context.refetch();
        }
    };

    return (
        <DialogBase
            title="Paste tags from spreadsheet"
            type="Custom"
            visible
            width="780px"
            onClose={() => onClose()}
            validationError={validationError || undefined}
            actions={(
                <>
                    <button className="btn btn-secondary" onClick={() => onClose()} disabled={importResult.loading}>Cancel</button>
                    <button className="btn btn-primary" onClick={submit} disabled={!rows.length || errorCount > 0 || context.loading || importResult.loading}>
                        Import {rows.length || ''} tag{rows.length === 1 ? '' : 's'}
                    </button>
                </>
            )}
            data-testid="ImportTagsDialog"
        >
            <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Tag Type, Tag Name, Description</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>Copy these three columns from a spreadsheet. Existing and archived tags cannot be overwritten.</p>
                </div>
                <button className="btn btn-secondary inline-flex items-center gap-2 whitespace-nowrap" onClick={paste} disabled={context.loading}>
                    <ClipboardPaste size={15} /> Paste
                </button>
            </div>

            {rows.length > 0 && (
                <>
                    <div className="flex items-center gap-2 mb-3 text-xs" style={{ color: errorCount ? 'var(--danger)' : 'var(--success)' }}>
                        {errorCount ? <AlertTriangle size={14} /> : <Check size={14} />}
                        {errorCount ? `${errorCount} row${errorCount === 1 ? '' : 's'} must be corrected or removed` : 'All rows are ready to import'}
                    </div>
                    <div className="overflow-auto border rounded-md" style={{ borderColor: 'var(--border-default)', maxHeight: 420 }}>
                        <table className="w-full text-sm">
                            <thead className="sticky top-0" style={{ background: 'var(--bg-subtle)' }}>
                                <tr>
                                    <th className="px-3 py-2 text-left">Tag Type</th>
                                    <th className="px-3 py-2 text-left">Tag Name</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <th className="px-3 py-2 text-left">Validation</th>
                                    <th className="w-10" />
                                </tr>
                            </thead>
                            <tbody>
                                {validatedRows.map(row => (
                                    <tr key={row.index} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                        <td className="px-3 py-2">{row.type || '—'}</td>
                                        <td className="px-3 py-2 font-medium">{row.name || '—'}</td>
                                        <td className="px-3 py-2">{row.description || '—'}</td>
                                        <td className="px-3 py-2 text-xs" style={{ color: row.errors.length ? 'var(--danger)' : 'var(--success)' }}>
                                            {row.errors.join('; ') || 'Ready'}
                                        </td>
                                        <td className="pr-2">
                                            <button className="btn btn-ghost p-1.5" title="Remove row" aria-label={`Remove row ${row.index + 1}`} onClick={() => setRows(current => current.filter((_, index) => index !== row.index))}>
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </DialogBase>
    );
};

export default ImportTagsDialog;